import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  Provider,
} from '@angular/core';
import {
  provideTransloco,
  TranslocoConfig,
  TranslocoService,
  TRANSLOCO_LOADING_TEMPLATE,
} from '@ngneat/transloco';
import { TranslocoMergeLoader } from '../loaders/transloco-merge-loader';
import { APP_I18N_NAMESPACE } from './app-namespace.token';

export interface ZopI18nConfig {
  /**
   * Application namespace for translation overrides
   * - 'zopup': No overrides (baseline)
   * - 'inflewz': Loads overrides/en.json
   * - 'verified': Loads overrides with kn.json support
   */
  appNamespace?: string;

  /**
   * Available languages (extends defaults)
   * Default: [{ id: 'en', label: 'English' }, { id: 'de', label: 'German' }]
   */
  availableLangs?: Array<{ id: string; label: string }>;

  /**
   * Default language
   * Default: 'en'
   */
  defaultLang?: string;

  /**
   * Fallback language
   * Default: 'en'
   */
  fallbackLang?: string;

  /**
   * Production mode (disables warnings)
   * Default: false
   */
  prodMode?: boolean;

  /**
   * Custom Transloco configuration (overrides defaults)
   */
  translocoConfig?: Partial<TranslocoConfig>;
}

/**
 * Provides Transloco configuration with merge loader support
 *
 * @param config - i18n configuration
 * @returns Environment providers for Transloco
 *
 * @example Basic usage (Zopup - no overrides)
 * ```typescript
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideZopI18n(), // Uses defaults
 *   ]
 * };
 * ```
 *
 * @example With app namespace (INFLEWZ)
 * ```typescript
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideZopI18n({
 *       appNamespace: 'inflewz',
 *     }),
 *   ]
 * };
 * ```
 *
 * @example With additional languages (Verified)
 * ```typescript
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideZopI18n({
 *       appNamespace: 'verified',
 *       availableLangs: [
 *         { id: 'en', label: 'English' },
 *         { id: 'de', label: 'German' },
 *         { id: 'kn', label: 'Kannada' },
 *       ],
 *     }),
 *   ]
 * };
 * ```
 */
export function provideZopI18n(
  config: ZopI18nConfig = {}
): EnvironmentProviders {
  const {
    appNamespace = 'zopup',
    availableLangs = [
      { id: 'en', label: 'English' },
      { id: 'de', label: 'German' },
    ],
    defaultLang = 'en',
    fallbackLang = 'en',
    prodMode = false,
    translocoConfig = {},
  } = config;

  const providers: Provider[] = [
    // App namespace for override detection
    {
      provide: APP_I18N_NAMESPACE,
      useValue: appNamespace,
    },

    // Transloco configuration with merge loader
    provideTransloco({
      config: {
        availableLangs,
        defaultLang,
        fallbackLang,
        reRenderOnLangChange: true,
        prodMode,
        missingHandler: {
          useFallbackTranslation: true,
          logMissingKey: !prodMode,
        },
        ...translocoConfig,
      },
      loader: TranslocoMergeLoader,
    }),

    // Loading template (optional - apps can override)
    {
      provide: TRANSLOCO_LOADING_TEMPLATE,
      useValue: `
        <div class="transloco-loading-template">
          <div class="flex items-center justify-center h-screen">
            <div class="flex flex-col items-center gap-4">
              <div class="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p class="text-secondary">Loading translations...</p>
            </div>
          </div>
        </div>
      `,
    },
  ];

  return makeEnvironmentProviders(providers);
}

/**
 * Helper function to preload translations before app initialization
 * Use in APP_INITIALIZER
 *
 * @example
 * ```typescript
 * import { provideAppInitializer } from '@angular/core';
 * import { preloadTranslations } from '@zopup/zop-i18n';
 *
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideZopI18n(),
 *     provideAppInitializer(() => preloadTranslations()),
 *   ]
 * };
 * ```
 */
export function preloadTranslations() {
  return () => {
    const translocoService = inject(TranslocoService);
    const defaultLang = translocoService.getDefaultLang();
    translocoService.setActiveLang(defaultLang);

    return new Promise<void>((resolve, reject) => {
      translocoService.load(defaultLang).subscribe({
        next: () => resolve(),
        error: (err) => {
          console.error('Error loading default language:', err);
          reject(err);
        },
      });
    });
  };
}
